import type { LinkProps as RacLinkProps } from 'react-aria-components';
import { Link as RacLink } from 'react-aria-components';
import type { LinkProps as RRRouterLinkProps } from 'react-router-dom';
import { Link as RRRouterLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import type { ReactNode } from 'react';

import styles from './index.module.scss';

type Sizes = 'x-small' | 'small' | 'medium' | 'large' | 'x-large' | '2x-large';
type Variants = 'primary' | 'secondary' | 'tertiary' | 'link';

type LinkProps = {
  size?: Sizes;
  variant?: Variants;
  leading?: ReactNode;
  iconLeading?: IconDefinition;
  iconTrailing?: IconDefinition;
  fullWidth?: boolean;
  underlined?: boolean;
} & RacLinkProps;

const sizeStyles: Record<Sizes, string> = {
  'x-small': styles.xSmall,
  small: styles.small,
  medium: styles.medium,
  large: styles.large,
  'x-large': styles.xLarge,
  '2x-large': styles.x2Large,
};

const variantStyles: Record<Variants, string> = {
  primary: styles.primary,
  secondary: styles.secondary,
  tertiary: styles.tertiary,
  link: styles.link,
};

export const Link = (props: LinkProps) => {
  const {
    children,
    className,
    size,
    variant,
    leading,
    iconLeading,
    iconTrailing,
    fullWidth,
    underlined,
    ...restProps
  } = props;

  const sizeStyle = sizeStyles[size ?? 'large'];
  const variantStyle = variantStyles[variant ?? 'link'];
  const fullWidthClass = fullWidth ? styles.fullWidth : '';
  const underlinedClass = underlined ? styles.underlined : '';

  return (
    <RacLink
      className={`${
        styles.button
      } ${sizeStyle} ${variantStyle} ${fullWidthClass} ${underlinedClass} ${
        className || ''
      }`}
      // Prevent keyboard event from propagating to parent elements that may have click handlers
      // (e.g. checkbox description with a link inside)
      // This may be resolved by updating RAC, but our current version doesn't appear to stop propagation of keyboard events for anchors
      onKeyDown={() => {}}
      {...restProps}
    >
      <>
        {leading}
        {iconLeading && (
          <FontAwesomeIcon
            icon={iconLeading}
            className={styles.leadingIcon}
            aria-hidden
          />
        )}
        {children}
        {iconTrailing && (
          <FontAwesomeIcon
            icon={iconTrailing}
            className={styles.trailingIcon}
            aria-hidden
          />
        )}
      </>
    </RacLink>
  );
};

type RouterLinkProps = {
  size?: Sizes;
  variant?: Variants;
  iconLeading?: IconDefinition;
  iconTrailing?: IconDefinition;
  fullWidth?: boolean;
  isDisabled?: LinkProps['isDisabled'];
} & RRRouterLinkProps;

export const RouterLink = (props: RouterLinkProps) => {
  const {
    to,
    children,
    size,
    variant,
    iconLeading,
    iconTrailing,
    fullWidth,
    isDisabled,
    ...restProps
  } = props;

  const sizeStyle = sizeStyles[size ?? 'large'];
  const variantStyle = variantStyles[variant ?? 'link'];
  const fullWidthClass = fullWidth ? styles.fullWidth : '';

  return (
    <RRRouterLink
      tabIndex={isDisabled ? -1 : undefined}
      to={isDisabled ? {} : to}
      data-disabled={isDisabled ? true : undefined}
      className={`${styles.button} ${sizeStyle} ${variantStyle} ${fullWidthClass}`}
      {...restProps}
    >
      <>
        {iconLeading && (
          <FontAwesomeIcon
            icon={iconLeading}
            className={styles.leadingIcon}
            aria-hidden
          />
        )}
        {children}
        {iconTrailing && (
          <FontAwesomeIcon
            icon={iconTrailing}
            className={styles.trailingIcon}
            aria-hidden
          />
        )}
      </>
    </RRRouterLink>
  );
};
